<script setup>
import { computed, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import PendenciaModal from './PendenciaModal.vue';
import PendenciaAPI from 'dashboard/api/fandata/pendencia';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const [showPendenciaModal, togglePendenciaModal] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);

// Pendência (Bloco 2 / T-02): estado vem de additional_attributes; atualiza ao vivo via conversation.updated.
const pendencia = computed(
  () => currentChat.value?.additional_attributes?.fandata_pendencia || null
);
const pendente = computed(() => !!pendencia.value);

// T-155f: os três pontinhos ficam só com "Enviar cópia do atendimento". Pedido literal da Tamara
// em 03/09: "a única opção desse menu tem que ser enviar cópia de atendimento".
//
// Saíram: bloquear/desbloquear contato e "encaminhar pelo Bira" (validação de 31/08, o agente não
// pode ter nenhum dos dois) e a pendência, que mudou de lugar em vez de sumir: agora vive no menu
// Resolver, porque marcar pendência é uma forma de encerrar o turno do agente, não uma ação
// paralela. Ver ResolveAction.
const actionMenuItems = computed(() => [
  {
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  },
]);

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

const desmarcarPendencia = async () => {
  try {
    await PendenciaAPI.desmarcar(currentChat.value.id);
    useAlert(t('CONVERSATION.PENDENCIA.RETOMADA'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('CONVERSATION.PENDENCIA.ERROR'));
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <span
      v-if="pendente"
      v-tooltip="pendencia?.nota || ''"
      class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-n-amber-3 text-n-amber-11 whitespace-nowrap"
    >
      {{
        $t('CONVERSATION.PENDENCIA.SELO', { parceiro: pendencia?.parceiro_nome })
      }}
    </span>
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
      :pendente="pendente"
      @marcar-pendencia="togglePendenciaModal"
      @retomar-pendencia="desmarcarPendencia"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <PendenciaModal
      v-if="showPendenciaModal"
      v-model:show="showPendenciaModal"
      :conversation-display-id="currentChat.id"
    />
  </div>
</template>
